import React, { useRef, useState, useEffect } from "react";
import { Sparkles, Play } from "lucide-react";

const R2 = "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev";

// Kundenprojekte + klar gekennzeichnete Demo-Spots, alle mit freycreate.ai generiert.
// Ketterer bewusst NICHT gelistet (inoffiziell, keine Markenfreigabe).
const OUTPUTS = [
  { id: "ki-seedos-spot",        title: "Seedos · Produkt-Spot",      tag: "Kunde" },
  { id: "ki-monitoris-steuer-a", title: "Monitoris · Steuer-Spot",    tag: "Kunde" },
  { id: "ki-monitoris-steuer-b", title: "Monitoris · Steuer-Spot II", tag: "Variante" },
  { id: "ki-monitoris-trainer",  title: "Monitoris · Trainer",        tag: "Kunde" },
  { id: "ki-koebi-testimonial",  title: "Koebi · Testimonial",        tag: "Testimonial" },
  { id: "ki-gold8-powder",       title: "Powder · Produkt-Ad",        tag: "Demo-Spot" },
];

// Video-Kachel: Poster als Standard, spielt nur bei Hover (Performance)
const OutputCard = ({ item }) => {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) setInView(true);
        else { videoRef.current?.pause(); setPlaying(false); }
      }),
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const start = () => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
    setPlaying(true);
  };
  const stop = () => { videoRef.current?.pause(); setPlaying(false); };

  return (
    <div
      ref={wrapperRef}
      className="group relative aspect-[9/16] rounded-xl overflow-hidden bg-black border border-white/10 hover:border-[hsl(var(--accent))]/60 transition-all hover:scale-[1.02] cursor-pointer"
      onMouseEnter={start}
      onMouseLeave={stop}
      onClick={() => (playing ? stop() : start())}
    >
      {inView ? (
        <video
          ref={videoRef}
          src={`${R2}/videos/${item.id}.mp4`}
          poster={`/thumbnails/${item.id}.webp`}
          muted
          loop
          playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img src={`/thumbnails/${item.id}.webp`} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      )}

      {/* Play-Hinweis solange nichts läuft */}
      {!playing && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="w-11 h-11 rounded-full bg-black/45 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-4.5 h-4.5 text-white ml-0.5" fill="white" />
          </div>
        </div>
      )}

      {/* Caption */}
      <div className="absolute inset-x-0 bottom-0 z-20 p-2.5 bg-gradient-to-t from-black/85 to-transparent">
        <div className="text-[11px] sm:text-xs text-white font-medium leading-snug">{item.title}</div>
        <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[hsl(var(--accent))] font-semibold">
          <Sparkles className="w-2.5 h-2.5" /> {item.tag}
        </div>
      </div>
    </div>
  );
};

const KiOutputs = () => (
  <section className="py-24 bg-background" data-testid="ki-outputs">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header — editorial, wie Portfolio/Lichtspiel */}
      <div className="mb-12 max-w-3xl">
        <div className="inline-flex items-center gap-3 mb-5 text-xs font-semibold uppercase tracking-[0.28em] text-[hsl(var(--accent))]">
          <Sparkles className="w-4 h-4" />
          <span className="w-10 h-px bg-[hsl(var(--accent))]/60" />
          Der Output
        </div>
        <h2 className="text-4xl sm:text-6xl font-bold leading-[1.03] tracking-tight">
          100% generiert mit <span className="text-[hsl(var(--accent))]">freycreate.ai</span>
        </h2>
        <p className="text-lg text-muted-foreground mt-5 max-w-xl">
          Keine Kamera, kein Set — komplett auf meiner KI-Plattform entstanden.
          Kundenprojekte und Demo-Spots. Hover für Vorschau.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
        {OUTPUTS.map((item) => (
          <OutputCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  </section>
);

export default KiOutputs;
